import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartShopping,
  faHouse,
  faUsers,
  faNewspaper,
  faPhone,
  faGraduationCap as faGraduationCapSolid,
  faPodcast,
  faBullhorn,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  CircleUserRound,
  ChevronLeft,
  ClipboardList,
  CreditCard,
  GraduationCap,
  Globe2,
  Headset,
  Home,
  Layers,
  LayoutDashboard,
  LogIn,
  Menu,
  Megaphone,
  MessageCircle,
  MessageSquareText,
  Mic2,
  Newspaper,
  Phone,
  ReceiptText,
  Search,
  ShoppingBag,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { MOCK_PODCAST_COLUMNS, MegaLinkItem, type MegaCategoryColumn } from '../../data/navMegaMock';
import { categoriesService, coursesService, leadsService } from '../../services/api';
import type { Course } from '../../types';
import { buildCourseMegaColumns } from '../../utils/courseMegaNav';
import { haystackIncludesFa, htmlToPlainSnippet } from '../../utils/faSearchNormalize';
import { getCoursePathSegment } from '../../utils/coursePath';
import { getImageUrl, getImageUrlWithFallback } from '../../utils/imageUtils';
import { normalizePhoneNumber } from '../../utils/phoneUtils';
import { BrandSpinnerRing } from '../SiteBrandedLoader';
import { FreeBadge } from '../FreeBadge';

type MegaKey = 'courses' | 'podcasts' | null;

type SimpleNavItem = { label: string; path: string; icon: LucideIcon; faIcon: IconDefinition };

const simpleNavItems: SimpleNavItem[] = [
  { label: 'خانه', path: '/', icon: Home, faIcon: faHouse },
  { label: 'درباره ما', path: '/about', icon: Users, faIcon: faUsers },
  { label: 'همراهان ما', path: '/companions-worldwide', icon: Globe2, faIcon: faUsers },
  { label: 'مقالات', path: '/articles', icon: Newspaper, faIcon: faNewspaper },
  { label: 'سفیر شو', path: '/ambassadors/join', icon: Megaphone, faIcon: faBullhorn },
  { label: 'تماس با ما', path: '/contact', icon: MessageCircle, faIcon: faPhone },
];

const navAfterHome = simpleNavItems.filter((item) => item.path !== '/');

type CartPreviewLine = {
  id: string;
  title: string;
  subtitle: string;
  /** قیمت نهایی بعد از تخفیف (تومان) */
  price: number;
  /** قیمت قبل از تخفیف */
  listPrice: number;
  /** تصویر دوره / محصول */
  thumbnail?: string;
};

function formatToman(n: number): string {
  return `${n.toLocaleString('fa-IR')} تومان`;
}

function coursePayableTomans(c: Course): number {
  const v = c.effectivePrice ?? c.price;
  return typeof v === 'number' ? v : Number(v) || 0;
}

function courseListTomans(c: Course): number {
  const v = c.listPrice ?? c.price;
  return typeof v === 'number' ? v : Number(v) || 0;
}

function courseHeaderDiscountPercent(c: Course): number {
  if (c.hasActiveDiscount && c.discountType === 'PERCENT' && c.discountPercent != null) {
    const p = Math.round(Number(c.discountPercent));
    if (Number.isFinite(p) && p > 0 && p <= 100) return p;
  }
  const pay = coursePayableTomans(c);
  const list = courseListTomans(c);
  if (list > pay && pay >= 0 && list > 0) {
    const implied = Math.round((1 - pay / list) * 100);
    if (implied > 0 && implied <= 100) return implied;
  }
  return 0;
}

function courseHeaderShowDiscount(c: Course): boolean {
  if (c.hasActiveDiscount) return true;
  const pay = coursePayableTomans(c);
  const list = courseListTomans(c);
  return list > pay && pay >= 0 && list > 0;
}

function CartPopoverSkeleton() {
  return (
    <div className="space-y-3 p-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex animate-pulse gap-2.5">
          <div className="h-14 w-14 shrink-0 rounded-lg bg-slate-200/90" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-[88%] rounded bg-slate-200" />
            <div className="h-2.5 w-[55%] rounded bg-slate-100" />
            <div className="flex gap-2 pt-1">
              <div className="h-3 w-16 rounded bg-slate-100" />
              <div className="h-3 w-20 rounded bg-slate-200" />
            </div>
          </div>
          <div className="h-8 w-8 shrink-0 self-start rounded-lg bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function HeaderSearchHitsSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <ul className="divide-y divide-slate-100 py-1" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex gap-3.5 px-3 py-2.5">
          <div className="aspect-[16/9] w-[5.5rem] shrink-0 animate-pulse rounded-lg bg-slate-200/90 sm:w-24" />
          <div className="min-w-0 flex-1 space-y-2 pt-0.5">
            <div className="h-2.5 w-14 animate-pulse rounded bg-slate-100" />
            <div className="h-3.5 w-[92%] animate-pulse rounded bg-slate-200" />
            <div className="flex min-w-0 items-center justify-between gap-2 pt-1">
              <div className="h-2.5 w-[36%] min-w-[3rem] max-w-[7rem] animate-pulse rounded bg-slate-100" />
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="h-3.5 w-16 animate-pulse rounded bg-slate-200/80" />
                <div className="h-3 w-11 animate-pulse rounded bg-slate-100" />
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

const underlineTransition = 'transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]';
/** خط زیر لینک هدر — بنفش و ضخیم‌تر طبق تم */
const NAV_UNDERLINE_CLASS =
  'pointer-events-none absolute bottom-0 left-1 right-1 h-[3px] origin-center rounded-sm bg-[#7c3aed]';

/** بوردر بنفش کنترل‌های برجستهٔ ردیف بالای هدر */
const HEADER_CTRL_BTN_BORDER =
  'border border-[#c4b5fd] shadow-sm transition-colors hover:border-[#a78bfa] hover:bg-violet-50/50 focus-visible:border-[#9333ea] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a78bfa]/30';

const normalizeLeadTextInput = (value: string) =>
  value
    // Convert common Persian/Arabic spacing chars to regular space
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
    // Convert ZWNJ/ZWSP to regular space for better UX in names/sentences
    .replace(/[\u200B\u200C]/g, ' ')
    // Remove directional marks
    .replace(/[\u200E\u200F]/g, '');

/** آیکن ردیف منوی موبایل — هاور فقط روی آیکن (والد: `group/navrow`) */
function MobileDrawerRowIcon({ icon: Icon, active }: { icon: LucideIcon; active?: boolean }) {
  if (active) {
    return (
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#1C3B9B]/25 bg-gradient-to-br from-[#1C3B9B]/12 to-[#C11277]/10 text-[#1C3B9B]"
        aria-hidden
      >
        <Icon className="h-[1.05rem] w-[1.05rem]" strokeWidth={2} />
      </span>
    );
  }
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-100 bg-slate-50 text-slate-600 shadow-none transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/navrow:scale-105 group-hover/navrow:border-[#1C3B9B]/35 group-hover/navrow:text-[#1C3B9B] group-hover/navrow:shadow-[0_8px_22px_-10px_rgba(28,59,155,0.38)]"
      aria-hidden
    >
      <Icon className="h-[1.05rem] w-[1.05rem]" strokeWidth={2} />
    </span>
  );
}

const ChevronDown: React.FC<{ open: boolean; className?: string }> = ({ open, className = '' }) => (
  <svg
    className={`h-3.5 w-3.5 shrink-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? 'rotate-180' : ''} ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const desktopNavIconBase =
  'inline-flex h-4 w-4 shrink-0 items-center justify-center text-slate-500 transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]';

const desktopNavIconHoverNav =
  'group-hover/nav:text-[#1C3B9B]';

const desktopNavIconHoverMega =
  'group-hover/mega:text-[#7c3aed]';

function NavLinkUnderline({
  children,
  faIcon,
  active,
  onClick,
}: {
  children: React.ReactNode;
  faIcon?: IconDefinition;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group/nav relative flex items-center gap-1.5 px-2.5 pb-1.5 pt-1 text-sm font-semibold text-slate-600 transition-colors duration-300 group-hover/nav:text-[#7c3aed]"
    >
      {faIcon ? (
        <span
          className={`${desktopNavIconBase} ${
            active
              ? 'text-[#7c3aed]'
              : desktopNavIconHoverNav
          }`}
          aria-hidden
        >
          <FontAwesomeIcon icon={faIcon} className="h-3.5 w-3.5" />
        </span>
      ) : null}
      <span className="relative z-10">{children}</span>
      <span
        className={`${NAV_UNDERLINE_CLASS} ${underlineTransition} ${
          active ? 'scale-x-100' : 'scale-x-0 group-hover/nav:scale-x-100'
        }`}
        aria-hidden
      />
    </button>
  );
}

function MegaNavTrigger({
  children,
  faIcon,
  open,
  active,
  onMouseEnter,
  onFocus,
  onClick,
}: {
  children: React.ReactNode;
  faIcon?: IconDefinition;
  open: boolean;
  active: boolean;
  onMouseEnter: () => void;
  onFocus: () => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      onClick={onClick}
      className={`group/mega relative flex items-center gap-1.5 px-2.5 pb-1.5 pt-1 text-sm font-bold transition-colors duration-300 ${
        open || active ? 'text-[#7c3aed]' : 'text-slate-600 group-hover/mega:text-[#7c3aed]'
      }`}
    >
      <span className="relative z-10 flex items-center gap-1.5">
        {faIcon ? (
          <span
            className={`${desktopNavIconBase} ${
              open || active
                ? 'text-[#7c3aed]'
                : desktopNavIconHoverMega
            }`}
            aria-hidden
          >
            <FontAwesomeIcon icon={faIcon} className="h-3.5 w-3.5" />
          </span>
        ) : null}
        {children}
      </span>
      <span
        className={`${NAV_UNDERLINE_CLASS} ${underlineTransition} ${
          open || active ? 'scale-x-100' : 'scale-x-0 group-hover/mega:scale-x-100'
        }`}
        aria-hidden
      />
    </button>
  );
}

function MegaLinkThumb({ item, size }: { item: MegaLinkItem; size: 'md' | 'sm' }) {
  const dim = size === 'md' ? 'h-10 w-10' : 'h-9 w-9';
  const url = item.thumbnailUrl ?? null;
  const base =
    `${dim} shrink-0 overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-200/85 ` +
    `flex items-center justify-center`;

  if (url) {
    return (
      <span className={base} aria-hidden>
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
      </span>
    );
  }
  const browse = item.id === 'course-mega-browse';
  return (
    <span className={`${base} text-slate-400`} aria-hidden>
      {browse ? (
        <GraduationCap className={size === 'md' ? 'h-[1.125rem] w-[1.125rem]' : 'h-4 w-4'} strokeWidth={2} />
      ) : (
        <Layers className={size === 'md' ? 'h-[1.125rem] w-[1.125rem]' : 'h-4 w-4'} strokeWidth={2} />
      )}
    </span>
  );
}

function MegaLinkRow({
  item,
  onPick,
}: {
  item: MegaLinkItem;
  onPick: (href: string) => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      dir="rtl"
      onClick={() => onPick(item.href)}
      className="group/link flex w-full items-stretch gap-2.5 rounded-xl border border-slate-100/90 bg-white/90 px-3 py-2.5 text-start shadow-[0_1px_3px_rgba(15,23,42,0.04)] transition-all duration-200 hover:border-violet-200/70 hover:bg-gradient-to-bl hover:from-violet-50/40 hover:to-white hover:shadow-[0_8px_28px_-12px_rgba(124,58,237,0.15)]"
    >
      <MegaLinkThumb item={item} size="md" />
      <span className="min-w-0 flex-1 space-y-0.5">
        <span className="flex flex-wrap items-center justify-start gap-1.5">
          <span className="text-sm font-bold text-slate-800 transition-colors group-hover/link:text-[#5b21b6]">
            {item.label}
          </span>
          {item.badge && (
            <span className="rounded-full bg-violet-100/90 px-2 py-px text-[10px] font-bold text-violet-800 ring-1 ring-violet-200/80">
              {item.badge}
            </span>
          )}
        </span>
        {item.description ? (
          <span className="block text-start text-[11px] font-medium leading-relaxed tracking-tight text-slate-500 group-hover/link:text-slate-600">
            {item.description}
          </span>
        ) : null}
      </span>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-lg bg-slate-100/80 text-slate-400 transition-colors group-hover/link:bg-violet-100 group-hover/link:text-[#7c3aed]">
        <ChevronLeft className="h-4 w-4 opacity-75" strokeWidth={2.25} aria-hidden />
      </span>
    </motion.button>
  );
}

function MegaColumns({
  columns,
  onPick,
}: {
  columns: MegaCategoryColumn[];
  onPick: (href: string) => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-3" dir="rtl">
      {columns.map((col) => (
        <div
          key={col.id}
          dir="rtl"
          lang="fa"
          className="rounded-2xl border border-slate-200/60 bg-white p-3.5 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/[0.025]"
        >
          <header className="mb-3 border-b border-slate-100 pb-3 text-start">
            <p className="text-base font-extrabold tracking-tight text-slate-900">{col.title}</p>
            {col.subtitle ? (
              <p className="mt-1 text-[11px] font-semibold leading-relaxed text-slate-500">{col.subtitle}</p>
            ) : null}
          </header>
          <div className="flex flex-col gap-1.5">
            {col.items.map((item) => (
              <MegaLinkRow key={item.id} item={item} onPick={onPick} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LoginEntryButton({
  onClick,
  compact,
  fullWidth,
  mobileHero,
}: {
  onClick: () => void;
  compact?: boolean;
  fullWidth?: boolean;
  mobileHero?: boolean;
}) {
  const size =
    mobileHero && fullWidth
      ? 'h-12 w-full rounded-xl px-4 text-[15px] font-semibold tracking-tight'
      : fullWidth
        ? 'h-10 w-full rounded-lg px-3 text-sm font-semibold'
        : compact
          ? 'h-9 min-w-[7.25rem] rounded-lg px-2.5 text-xs font-semibold'
          : 'h-9 min-w-[9rem] rounded-lg px-3 text-sm font-semibold sm:min-w-[9.75rem]';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center bg-white text-slate-800 ${HEADER_CTRL_BTN_BORDER} ${
        fullWidth ? 'flex w-full' : 'shrink-0'
      } ${size}`}
      aria-label="ورود یا ثبت نام"
    >
      <span className="flex items-center gap-1.5">
        <LogIn className="h-3.5 w-3.5 text-slate-500" strokeWidth={2.25} aria-hidden />
        ورود / ثبت نام
      </span>
    </button>
  );
}

const HeaderBar: React.FC = () => {
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState<MegaKey>(null);
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
  const [mobilePodcastsOpen, setMobilePodcastsOpen] = useState(false);
  /** مگامنوی دوره‌ها — از API + لینک کاوش */
  const [courseMegaColumns, setCourseMegaColumns] = useState<MegaCategoryColumn[]>(() => buildCourseMegaColumns([]));
  const [searchDraft, setSearchDraft] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [searchCourseCatalog, setSearchCourseCatalog] = useState<Course[] | null>(null);
  const [searchCatalogBusy, setSearchCatalogBusy] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [leadIntroActive, setLeadIntroActive] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [leadSuccess, setLeadSuccess] = useState('');
  const [leadForm, setLeadForm] = useState({
    fullName: '',
    phone: '',
    interest: '',
    note: '',
  });
  const navLinksExpanded = true;
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);
  const [cartPreviewLoading, setCartPreviewLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { items, removeItem } = useCart();
  const navRootRef = useRef<HTMLDivElement>(null);
  const mobilePortalRef = useRef<HTMLDivElement | null>(null);
  const leadPortalRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const cartHoverTimerRef = useRef<number | null>(null);
  const cartLoadTimerRef = useRef<number | null>(null);
  const leadIntroTimerRef = useRef<number | null>(null);
  const searchBlurTimerRef = useRef<number | null>(null);
  const searchCatalogInflightRef = useRef(false);

  const cartLines: CartPreviewLine[] = items;
  const cartCount = cartLines.length;

  const cartTotals = useMemo(() => {
    const listSum = cartLines.reduce((s, l) => s + l.listPrice, 0);
    const paySum = cartLines.reduce((s, l) => s + l.price, 0);
    return { listSum, paySum, saved: listSum - paySum };
  }, [cartLines]);

  const cartPayableTitle = useMemo(() => {
    if (cartLines.length === 0) return 'سبد خرید';
    return cartTotals.paySum <= 0 ? `سبد خرید — رایگان (${cartCount})` : `سبد خرید — ${formatToman(cartTotals.paySum)} (${cartCount})`;
  }, [cartLines.length, cartTotals.paySum, cartCount]);

  const removeCartItem = useCallback((id: string) => {
    removeItem(id);
  }, [removeItem]);

  const cancelMegaClose = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleMegaClose = useCallback(() => {
    cancelMegaClose();
    closeTimerRef.current = window.setTimeout(() => setMegaOpen(null), 220);
  }, [cancelMegaClose]);

  const cancelCartHoverClose = useCallback(() => {
    if (cartHoverTimerRef.current != null) {
      window.clearTimeout(cartHoverTimerRef.current);
      cartHoverTimerRef.current = null;
    }
  }, []);

  const scheduleCartHoverClose = useCallback(() => {
    cancelCartHoverClose();
    cartHoverTimerRef.current = window.setTimeout(() => setCartPreviewOpen(false), 180);
  }, [cancelCartHoverClose]);

  const openMega = useCallback(
    (key: Exclude<MegaKey, null>) => {
      cancelMegaClose();
      setMegaOpen(key);
    },
    [cancelMegaClose],
  );

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const isCoursesSection =
    location.pathname.startsWith('/courses') || location.pathname.startsWith('/workshops');
  const isPodcastsSection =
    location.pathname.startsWith('/podcasts') || location.pathname.startsWith('/video-podcasts');

  const closeAllOverlays = useCallback(() => {
    if (leadIntroTimerRef.current != null) {
      window.clearTimeout(leadIntroTimerRef.current);
      leadIntroTimerRef.current = null;
    }
    if (searchBlurTimerRef.current != null) {
      window.clearTimeout(searchBlurTimerRef.current);
      searchBlurTimerRef.current = null;
    }
    cancelMegaClose();
    cancelCartHoverClose();
    setMegaOpen(null);
    setCartPreviewOpen(false);
    setIsUserMenuOpen(false);
    setMobileDrawerOpen(false);
    setMobileCoursesOpen(false);
    setMobilePodcastsOpen(false);
    setIsLeadModalOpen(false);
    setLeadIntroActive(false);
    setSearchPanelOpen(false);
  }, [cancelMegaClose, cancelCartHoverClose]);

  useEffect(() => {
    closeAllOverlays();
  }, [location.pathname, closeAllOverlays]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAllOverlays();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeAllOverlays]);

  /** دستهٔ دوره برای مگامنو و منوی موبایل — GET /categories?type=COURSE */
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const list = await categoriesService.list('COURSE');
        if (!alive) return;
        setCourseMegaColumns(buildCourseMegaColumns(Array.isArray(list) ? list : []));
      } catch {
        if (!alive) return;
        setCourseMegaColumns(buildCourseMegaColumns([]));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const root = navRootRef.current;
      const t = e.target as Node;
      if (!root) return;
      if (
        root.contains(t) ||
        mobilePortalRef.current?.contains(t) ||
        leadPortalRef.current?.contains(t)
      )
        return;
      cancelMegaClose();
      cancelCartHoverClose();
      setMegaOpen(null);
      setCartPreviewOpen(false);
      setIsUserMenuOpen(false);
      setMobileDrawerOpen(false);
      setSearchPanelOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [cancelMegaClose, cancelCartHoverClose]);

  useEffect(() => {
    if (!mobileDrawerOpen && !isLeadModalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileDrawerOpen, isLeadModalOpen]);

  /** شبیه‌سازی بارگذاری زنده هنگام باز شدن پنل سبد */
  useEffect(() => {
    if (!cartPreviewOpen) {
      setCartPreviewLoading(false);
      if (cartLoadTimerRef.current != null) {
        window.clearTimeout(cartLoadTimerRef.current);
        cartLoadTimerRef.current = null;
      }
      return;
    }
    setCartPreviewLoading(true);
    cartLoadTimerRef.current = window.setTimeout(() => {
      setCartPreviewLoading(false);
      cartLoadTimerRef.current = null;
    }, 480);
    return () => {
      if (cartLoadTimerRef.current != null) {
        window.clearTimeout(cartLoadTimerRef.current);
        cartLoadTimerRef.current = null;
      }
    };
  }, [cartPreviewOpen]);

  useEffect(() => {
    const id = window.setTimeout(() => setSearchDebounced(searchDraft.trim()), 300);
    return () => window.clearTimeout(id);
  }, [searchDraft]);

  useEffect(() => {
    return () => {
      if (searchBlurTimerRef.current != null) window.clearTimeout(searchBlurTimerRef.current);
    };
  }, []);

  const ensureSearchCatalog = useCallback(async () => {
    if (searchCourseCatalog != null || searchCatalogInflightRef.current) return;
    searchCatalogInflightRef.current = true;
    setSearchCatalogBusy(true);
    try {
      const crs = await coursesService.getPublished();
      setSearchCourseCatalog(Array.isArray(crs) ? crs : []);
    } catch {
      setSearchCourseCatalog([]);
    } finally {
      searchCatalogInflightRef.current = false;
      setSearchCatalogBusy(false);
    }
  }, [searchCourseCatalog]);

  const searchHits = useMemo(() => {
    if (!searchCourseCatalog || searchDebounced.length < 2) return [] as Course[];
    const q = searchDebounced;
    return searchCourseCatalog
      .filter((c) => {
        const descPlain = `${htmlToPlainSnippet(c.description, 260)} ${htmlToPlainSnippet(c.longDescription ?? '', 260)}`;
        return (
          haystackIncludesFa(c.title, q) ||
          haystackIncludesFa(c.category?.name ?? '', q) ||
          haystackIncludesFa(descPlain, q)
        );
      })
      .slice(0, 8);
  }, [searchCourseCatalog, searchDebounced]);

  const onSearchFocus = useCallback(() => {
    if (searchBlurTimerRef.current != null) {
      window.clearTimeout(searchBlurTimerRef.current);
      searchBlurTimerRef.current = null;
    }
    setSearchPanelOpen(true);
    void ensureSearchCatalog();
  }, [ensureSearchCatalog]);

  const scheduleSearchBlurClose = useCallback(() => {
    if (searchBlurTimerRef.current != null) window.clearTimeout(searchBlurTimerRef.current);
    searchBlurTimerRef.current = window.setTimeout(() => {
      setSearchPanelOpen(false);
      searchBlurTimerRef.current = null;
    }, 170);
  }, []);

  const goNav = (path: string) => {
    closeAllOverlays();
    navigate(path);
  };

  const goCart = () => {
    closeAllOverlays();
    navigate('/cart');
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchDraft.trim();
    closeAllOverlays();
    if (q) navigate(`/courses?q=${encodeURIComponent(q)}`);
    else navigate('/courses');
  };

  const pickCourseSearchHit = (c: Course) => {
    closeAllOverlays();
    navigate(`/courses/${getCoursePathSegment(c)}`);
  };

  const openLeadModal = () => {
    if (leadIntroTimerRef.current != null) {
      window.clearTimeout(leadIntroTimerRef.current);
      leadIntroTimerRef.current = null;
    }
    setLeadError('');
    setLeadSuccess('');
    const canShowIntro = true;
    setLeadIntroActive(canShowIntro);
    setIsLeadModalOpen(true);
    if (canShowIntro) {
      leadIntroTimerRef.current = window.setTimeout(() => {
        setLeadIntroActive(false);
        leadIntroTimerRef.current = null;
      }, 1500);
    }
  };

  const closeLeadModal = () => {
    if (leadIntroTimerRef.current != null) {
      window.clearTimeout(leadIntroTimerRef.current);
      leadIntroTimerRef.current = null;
    }
    setLeadIntroActive(false);
    setIsLeadModalOpen(false);
  };

  const handleLeadInputChange = (field: 'fullName' | 'phone' | 'interest' | 'note', value: string) => {
    const nextValue =
      field === 'phone'
        ? value
        : value
            // Keep regular spaces; normalize uncommon whitespace chars from some keyboards
            .replace(/[\u00A0\u202F]/g, ' ')
            .replace(/[\u200B\u200C]/g, ' ');

    setLeadForm((prev) => ({ ...prev, [field]: nextValue }));
    if (leadError) {
      setLeadError('');
    }
  };

  const handleLeadSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLeadError('');
    setLeadSuccess('');

    const normalizedPhone = normalizePhoneNumber(leadForm.phone).replace(/\s+/g, '');
    const normalizedFullName = normalizeLeadTextInput(leadForm.fullName).trim();
    const normalizedInterest = normalizeLeadTextInput(leadForm.interest).trim();
    const normalizedNote = normalizeLeadTextInput(leadForm.note).trim();
    const isPhoneValid = /^(\+98|0)?9\d{9}$/.test(normalizedPhone);

    if (!normalizedFullName) {
      setLeadError('نام و نام خانوادگی را وارد کنید.');
      return;
    }
    if (!isPhoneValid) {
      setLeadError('شماره موبایل معتبر نیست.');
      return;
    }

    setLeadLoading(true);
    try {
      await leadsService.create({
        fullName: normalizedFullName,
        phone: normalizedPhone,
        interest: normalizedInterest || undefined,
        note: normalizedNote || undefined,
        source: 'HEADER_BAR_CTA',
      });
      setLeadSuccess('درخواست شما با موفقیت ثبت شد.');
      setLeadForm({ fullName: '', phone: '', interest: '', note: '' });
      window.setTimeout(() => {
        closeLeadModal();
        setLeadSuccess('');
      }, 1200);
    } catch (error: any) {
      setLeadError(error?.response?.data?.message || 'خطا در ثبت درخواست. لطفا دوباره تلاش کنید.');
    } finally {
      setLeadLoading(false);
    }
  };

  const renderSearchCluster = (formExtraClass: string) => {
    const debouncing = searchDraft.trim().length >= 2 && searchDraft.trim() !== searchDebounced;
    const showHitsSkeleton =
      searchCatalogBusy || (debouncing && searchCourseCatalog != null && searchDraft.trim().length >= 2);
    const totalHits = searchHits.length;

    return (
      <div className={`relative min-w-0 ${formExtraClass}`}>
        <form
          onSubmit={submitSearch}
          className="group/search relative flex min-w-0 items-center"
          role="search"
        >
          <Search
            className="pointer-events-none absolute end-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors duration-200 group-focus-within/search:text-slate-600"
            strokeWidth={2.25}
            aria-hidden
          />
          <input
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onFocus={onSearchFocus}
            onBlur={scheduleSearchBlurClose}
            placeholder="جستجو در دوره‌ها…"
            className="w-full rounded-lg border border-slate-200/90 bg-slate-50/90 py-2 ps-3 pe-10 text-sm text-slate-800 transition-colors placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-slate-200"
            aria-label="جستجو در دوره‌ها"
            autoComplete="off"
            spellCheck={false}
          />
        </form>

        <AnimatePresence>
          {searchPanelOpen && (
            <motion.div
              dir="rtl"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-0 top-full z-[70] mt-1.5 overflow-hidden rounded-xl border border-slate-200/85 bg-white shadow-[0_20px_44px_-14px_rgba(15,23,42,0.22)] ring-1 ring-slate-900/[0.04] outline-none backdrop-blur-sm"
              onMouseDown={(e) => e.preventDefault()}
              role="listbox"
              aria-label="پیشنهادهای جستجوی دوره"
            >
              <div className="max-h-[min(72vh,420px)] overflow-y-auto overscroll-contain px-2 py-2 [scrollbar-width:thin]">
                {searchCatalogBusy && searchCourseCatalog === null ? (
                  <HeaderSearchHitsSkeleton rows={6} />
                ) : searchCourseCatalog && searchDebounced.length < 2 ? (
                  <p className="px-3 py-6 text-center text-xs leading-relaxed text-slate-500">
                    حداقل دو کاراکتر برای جستجو در بین دوره‌ها تایپ کنید.
                  </p>
                ) : showHitsSkeleton ? (
                  <HeaderSearchHitsSkeleton rows={6} />
                ) : totalHits === 0 ? (
                  <p className="px-3 py-8 text-center text-xs text-slate-500">دوره‌ای یافت نشد.</p>
                ) : (
                  <div className="space-y-1">
                    <div>
                      <p className="px-3 pb-1 pt-0.5 text-start text-[10px] font-extrabold tracking-wide text-slate-500">
                        دوره‌ها
                      </p>
                      <ul className="space-y-0.5">
                        {searchHits.map((c) => {
                          const img = getImageUrl(c.thumbnail);
                          const pay = coursePayableTomans(c);
                          const list = courseListTomans(c);
                          const showDisc = courseHeaderShowDiscount(c);
                          const pct = courseHeaderDiscountPercent(c);
                          return (
                            <li key={c.id}>
                              <button
                                type="button"
                                className="flex w-full gap-3.5 rounded-lg px-2 py-2.5 text-start transition-colors hover:bg-slate-50"
                                onClick={() => pickCourseSearchHit(c)}
                              >
                                <div className="relative aspect-[16/9] w-[5.5rem] shrink-0 overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-200/80 sm:w-24">
                                  {img ? (
                                    <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
                                  ) : (
                                    <span className="absolute inset-0 flex items-center justify-center text-slate-400">
                                      <GraduationCap className="h-6 w-6" strokeWidth={1.75} aria-hidden />
                                    </span>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-medium text-slate-400">دوره آموزشی</p>
                                  <p className="line-clamp-2 text-sm font-semibold text-slate-800">{c.title}</p>
                                  <div className="mt-1 flex min-h-[1.25rem] min-w-0 flex-nowrap items-center justify-between gap-2">
                                    <p className="min-w-0 flex-1 truncate text-[11px] text-slate-500">
                                      {c.category?.name ?? '\u00a0'}
                                    </p>
                                    <div className="flex shrink-0 flex-nowrap items-center gap-1.5 whitespace-nowrap">
                                      {pay <= 0 ? (
                                        <FreeBadge size="sm" className="shadow-none ring-[#1C3B9B]/[0.09]" />
                                      ) : (
                                        <>
                                          <span className="text-sm font-extrabold leading-none tabular-nums text-[#1C3B9B]">
                                            {formatToman(pay)}
                                          </span>
                                          {showDisc && list > pay ? (
                                            <span className="text-[11px] leading-none tabular-nums text-slate-400 line-through decoration-slate-300">
                                              {formatToman(list)}
                                            </span>
                                          ) : null}
                                          {pct > 0 ? (
                                            <span className="rounded-md bg-[#C11277]/12 px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums text-[#C11277]">
                                              ٪{pct.toLocaleString('fa-IR')} تخفیف
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-slate-100 bg-slate-50/80 px-3 py-2">
                <button
                  type="button"
                  className="w-full rounded-lg py-1.5 text-center text-[11px] font-semibold text-slate-600 transition hover:bg-white hover:text-[#1C3B9B]"
                  onClick={() => {
                    const q = searchDraft.trim();
                    closeAllOverlays();
                    if (q) navigate(`/courses?q=${encodeURIComponent(q)}`);
                    else navigate('/courses');
                  }}
                >
                  مشاهده همه در کتابخانه دوره‌ها
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const cartButtonClass = `group/cart relative inline-flex h-9 shrink-0 items-center justify-center rounded-lg bg-white px-2.5 text-slate-600 ${HEADER_CTRL_BTN_BORDER} hover:text-[#1C3B9B]`;
  const dashboardQuickLinks = [
    { label: 'خانه داشبورد', subtitle: 'نمای کلی حساب', icon: LayoutDashboard, href: '/dashboard' },
    { label: 'دوره‌های من', subtitle: 'دوره‌های خریداری‌شده', icon: GraduationCap, href: '/dashboard?tab=courses' },
    { label: 'کیف پول', subtitle: 'اعتبار و تراکنش‌ها', icon: CreditCard, href: '/dashboard?tab=wallet' },
    { label: 'سفارش‌های من', subtitle: 'وضعیت خریدها', icon: ReceiptText, href: '/dashboard?tab=orders' },
    { label: 'پیام‌ها', subtitle: 'اعلان‌ها و پاسخ‌ها', icon: MessageSquareText, href: '/dashboard?tab=messages' },
    { label: 'پروفایل', subtitle: 'ویرایش اطلاعات', icon: CircleUserRound, href: '/dashboard?tab=profile' },
  ] as const;

  const leadModalOverlay = (
        <AnimatePresence>
          {isLeadModalOpen && (
            <motion.div
              ref={leadPortalRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[550] flex items-start justify-center overflow-x-hidden overflow-y-auto bg-slate-950/55 px-2 pb-6 pt-[4.5rem] backdrop-blur-[3px] sm:px-4 sm:pb-8 sm:pt-[5.25rem] lg:pt-[7.75rem]"
              onClick={closeLeadModal}
              dir="rtl"
            >
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.99 }}
                transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                onClick={(event) => event.stopPropagation()}
                className="relative isolate mx-auto my-auto max-h-[min(calc(100dvh-5.5rem),900px)] w-full min-w-0 max-w-2xl overflow-x-hidden overflow-y-auto overscroll-contain rounded-[1.65rem] border border-slate-200/80 bg-white shadow-[0_42px_120px_-52px_rgba(15,23,42,0.62)] sm:rounded-3xl lg:max-h-[min(calc(100dvh-9rem),900px)]"
              >
                <button
                  type="button"
                  onClick={closeLeadModal}
                  className="absolute end-3 top-3 z-30 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-600 shadow-md transition-colors hover:bg-slate-50 sm:end-4 sm:top-4 sm:h-10 sm:w-10"
                  aria-label="بستن"
                >
                  <X className="h-4 w-4" />
                </button>
                  <div
                    dir="rtl"
                    className="grid min-w-0 grid-cols-1 overflow-hidden rounded-[1.65rem] md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] sm:rounded-3xl"
                  >
                  <div
                    dir="rtl"
                      className="relative hidden min-w-0 overflow-hidden bg-gradient-to-b from-[#0b1738] via-[#132857] to-[#1C3B9B] p-5 pt-14 text-right text-white sm:p-6 sm:pt-16 md:block"
                  >
                    <span className="pointer-events-none absolute -left-14 top-10 h-44 w-44 rounded-full bg-[#93c5fd]/20 blur-2xl" />
                    <span className="pointer-events-none absolute -right-16 bottom-6 h-48 w-48 rounded-full bg-[#f9a8d4]/20 blur-2xl" />
                    <p className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[10px] font-semibold">
                      <Headset className="h-3.5 w-3.5 shrink-0 text-white/90" strokeWidth={2} aria-hidden />
                      شروع مشاوره
                    </p>
                    <h3 className="mt-4 text-2xl font-black leading-8">فرم اطلاعات مشتری</h3>
                    <p className="mt-2 text-sm leading-7 text-white/85">
                      اطلاعاتت رو ثبت کن تا تیم ما در کوتاه‌ترین زمان مسیر مناسب و برنامه ارتباطی دقیق رو برات آماده کنه.
                    </p>
                    <div className="mt-5 space-y-2.5">
                      <div className="flex items-start gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2.5 text-right text-xs leading-relaxed text-white/90">
                        <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-violet-200" aria-hidden />
                        <div>
                          <p className="font-bold">مسیر پیشنهادی اختصاصی</p>
                          <p className="mt-1 text-[11px] text-white/80">براساس نیاز و سطح فعلی شما پیشنهاد شخصی‌سازی می‌شود.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2.5 text-right text-xs leading-relaxed text-white/90">
                        <Phone className="mt-0.5 h-4 w-4 shrink-0 text-sky-200" aria-hidden />
                        <div>
                          <p className="font-bold">پیگیری حرفه‌ای و منظم</p>
                          <p className="mt-1 text-[11px] text-white/80">کارشناس مربوطه با برنامه دقیق و کوتاه‌ترین زمان پاسخ می‌دهد.</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 rounded-xl border border-cyan-200/20 bg-cyan-300/10 px-3 py-2 text-[11px] font-semibold text-cyan-100">
                      زمان تکمیل فرم کمتر از ۱ دقیقه
                    </div>
                  </div>

                    <div className="relative min-w-0 overflow-x-hidden p-4 pt-14 sm:p-6 sm:pt-16">
                      {leadIntroActive ? (
                        <div className="relative flex min-h-[25rem] max-w-full items-center justify-center overflow-hidden rounded-2xl border border-slate-200/70 bg-[#060d22] px-5 text-white sm:min-h-[27rem] sm:px-6">
                          <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_24%,rgba(34,211,238,0.24),transparent_42%),radial-gradient(circle_at_84%_70%,rgba(168,85,247,0.2),transparent_48%),radial-gradient(circle_at_55%_44%,rgba(59,130,246,0.2),transparent_58%)]" />
                          <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.05),transparent_30%,rgba(255,255,255,0.03)_52%,transparent_75%)]" />
                          <motion.span
                            initial={{ opacity: 0.25, scale: 1 }}
                            animate={{ opacity: [0.22, 0.44, 0.22], scale: [1, 1.12, 1] }}
                            transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }}
                            className="pointer-events-none absolute h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.4),rgba(37,99,235,0.02)_72%)] blur-2xl"
                          />
                          <motion.span
                            initial={{ opacity: 0.2, scale: 1 }}
                            animate={{ opacity: [0.16, 0.34, 0.16], scale: [1, 1.2, 1] }}
                            transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
                            className="pointer-events-none absolute h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.26),rgba(168,85,247,0.01)_72%)] blur-2xl"
                          />
                          <div className="relative z-10 flex flex-col items-center text-center">
                            <motion.div
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                              className="relative rounded-2xl border border-white/25 bg-white/[0.08] px-7 py-7 shadow-[0_30px_85px_-36px_rgba(20,42,99,0.92)] backdrop-blur-xl sm:px-8 sm:py-8"
                            >
                              <span className="pointer-events-none absolute inset-0 rounded-2xl bg-[linear-gradient(125deg,rgba(255,255,255,0.12),transparent_42%,rgba(255,255,255,0.08))]" />
                              <motion.span
                                initial={{ opacity: 0.35 }}
                                animate={{ opacity: [0.35, 0.8, 0.35] }}
                                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                                className="pointer-events-none absolute -inset-[1px] rounded-2xl border border-cyan-200/35"
                              />
                              <motion.span
                                initial={{ x: -90, opacity: 0 }}
                                animate={{ x: 100, opacity: [0, 0.9, 0] }}
                                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                                className="pointer-events-none absolute top-3 h-[2px] w-16 bg-gradient-to-r from-transparent via-white to-transparent"
                              />
                              <motion.span
                                initial={{ x: 95, opacity: 0 }}
                                animate={{ x: -80, opacity: [0, 0.8, 0] }}
                                transition={{ duration: 1.45, repeat: Infinity, ease: 'easeInOut', delay: 0.18 }}
                                className="pointer-events-none absolute bottom-3 h-[2px] w-14 bg-gradient-to-r from-transparent via-cyan-100 to-transparent"
                              />
                              <motion.span
                                initial={{ y: -40, opacity: 0 }}
                                animate={{ y: [0, -10, 0], opacity: [0.15, 0.45, 0.15] }}
                                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                                className="pointer-events-none absolute right-3 top-3 h-2 w-2 rounded-full bg-cyan-200 shadow-[0_0_14px_rgba(34,211,238,0.75)]"
                              />
                              <motion.span
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: [0, 8, 0], opacity: [0.12, 0.4, 0.12] }}
                                transition={{ duration: 1.9, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
                                className="pointer-events-none absolute bottom-4 left-5 h-2 w-2 rounded-full bg-violet-200 shadow-[0_0_14px_rgba(196,181,253,0.72)]"
                              />
                              <motion.span
                                initial={{ x: 0, opacity: 0.25 }}
                                animate={{ x: [0, 6, 0], opacity: [0.25, 0.65, 0.25] }}
                                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                                className="pointer-events-none absolute left-1/2 top-3 h-[3px] w-3 -translate-x-1/2 rounded-full bg-cyan-100"
                              />
                              <motion.img
                                src="/assets/logo-mane-haghighi-asli-1 (1).png"
                                alt="مرتضی حقیقی"
                                initial={{ scale: 0.86, opacity: 0 }}
                                animate={{ scale: [0.95, 1.03, 1], opacity: 1 }}
                                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                                className="mx-auto h-24 w-auto rounded-xl border border-white/30 bg-white/92 p-2.5 drop-shadow-[0_14px_24px_rgba(0,0,0,0.38)] sm:h-28"
                              />
                              <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45, delay: 0.15 }}
                                className="mt-4 flex items-center justify-center gap-1.5"
                              >
                                <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                                <motion.span
                                  className="h-1.5 w-1.5 rounded-full bg-white/90"
                                  animate={{ opacity: [0.35, 1, 0.35] }}
                                  transition={{ duration: 0.8, repeat: Infinity }}
                                />
                                <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                              </motion.div>
                            </motion.div>
                            <motion.p
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.45, delay: 0.2 }}
                              className="mt-5 text-sm font-medium text-white/88"
                            >
                              در حال آماده‌سازی فرم اختصاصی...
                            </motion.p>
                          </div>
                        </div>
                      ) : (
                        <form
                          dir="rtl"
                          onSubmit={handleLeadSubmit}
                          className="space-y-3 rounded-2xl border border-slate-200/80 bg-white p-3 text-right shadow-[0_20px_60px_-45px_rgba(15,23,42,0.5)] sm:space-y-3.5 sm:p-4"
                        >
                        <div>
                          <label className="mb-1 block text-[11px] font-bold text-slate-600">نام و نام خانوادگی</label>
                          <input
                            type="text"
                            dir="rtl"
                            value={leadForm.fullName}
                            onChange={(e) => handleLeadInputChange('fullName', e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 transition-all focus:border-[#1C3B9B]/35 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/15"
                            placeholder="مثال: مریم رضایی"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-bold text-slate-600">شماره موبایل</label>
                          <input
                            type="tel"
                            dir="ltr"
                            value={leadForm.phone}
                            onChange={(e) => handleLeadInputChange('phone', e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm text-slate-800 transition-all focus:border-[#1C3B9B]/35 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/15"
                            placeholder="09xxxxxxxxx"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-bold text-slate-600">موضوع علاقه مندی</label>
                          <input
                            type="text"
                            dir="rtl"
                            value={leadForm.interest}
                            onChange={(e) => handleLeadInputChange('interest', e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 transition-all focus:border-[#1C3B9B]/35 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/15"
                            placeholder="مثال: دوره جامع تحلیل تکنیکال"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-bold text-slate-600">توضیحات شما</label>
                          <textarea
                            rows={3}
                            dir="rtl"
                            value={leadForm.note}
                            onChange={(e) => handleLeadInputChange('note', e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 transition-all focus:border-[#1C3B9B]/35 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/15"
                            placeholder="نیاز، شرایط و توضیحات خود را بنویسید..."
                          />
                        </div>
                        {leadError ? (
                          <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{leadError}</p>
                        ) : null}
                        {leadSuccess ? (
                          <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                            {leadSuccess}
                          </p>
                        ) : null}
                        <button
                          type="submit"
                          disabled={leadLoading}
                          className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-4 py-3 text-sm font-black text-white transition hover:brightness-[1.03] disabled:opacity-60"
                        >
                          {leadLoading ? 'در حال ثبت...' : 'ارسال اطلاعات'}
                        </button>
                        </form>
                      )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
  );

  return (
    <>
    <nav className="relative z-50 overflow-x-clip border-b border-slate-200/90 bg-white/95 backdrop-blur-md lg:overflow-visible">
      <div ref={navRootRef} className="relative mx-auto max-w-7xl px-3 sm:px-4 lg:px-5">
        {/* ردیف ۱: لوگو + جستجو (نزدیک) | سبد + ورود — بدون justify-between افراطی */}
        <div className="flex items-center gap-2 border-b border-slate-100 py-1 sm:gap-2.5 lg:py-1">
          <button
            type="button"
            onClick={() => goNav('/')}
            className="group hidden shrink-0 rounded-xl p-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-[1.02] active:scale-[0.98] lg:block"
            aria-label="صفحه اصلی"
          >
            <img
              src="/assets/logo-mane-haghighi-asli-1 (1).png"
              alt="مرتضی حقیقی"
              className="h-12 w-auto object-contain sm:h-[3.25rem] md:h-[3.5rem] lg:h-[3.65rem]"
            />
          </button>

          {/* موبایل: سبد (چپ فیزیکی) | لوگوی بزرگ وسط ویوپورت | همبرگر مماس لبهٔ راست ویوپورت */}
          <div className="relative left-1/2 flex min-h-[2.95rem] w-screen max-w-[100vw] -translate-x-1/2 flex-1 items-center sm:min-h-[3.05rem] lg:hidden">
            <button
              type="button"
              onClick={() => goNav('/')}
              className="absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 rounded-2xl p-1 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.98]"
              aria-label="صفحه اصلی"
            >
              <img
                src="/assets/logo-mane-haghighi-asli-1 (1).png"
                alt="مرتضی حقیقی"
                className="h-[3.15rem] w-auto max-w-[min(56vw,16rem)] object-contain sm:h-[3.35rem] sm:max-w-[min(50vw,16.5rem)]"
              />
            </button>

            <button
              type="button"
              onClick={openLeadModal}
              className="absolute left-2 inset-y-0 z-20 my-auto inline-flex h-9 max-w-[min(5.5rem,28vw)] items-center justify-center gap-1 px-1.5 text-[10px] font-bold leading-none text-slate-700"
              aria-label="درخواست مشاوره"
            >
              <Headset className="h-3 w-3 shrink-0 text-slate-600" strokeWidth={2} aria-hidden />
              <span className="truncate">مشاوره</span>
            </button>

            <motion.button
              type="button"
              onClick={() => setMobileDrawerOpen((o) => !o)}
              className="absolute right-2 inset-y-0 z-20 my-auto inline-flex h-10 w-10 shrink-0 items-center justify-center leading-none text-slate-600 hover:text-[#1C3B9B]"
              aria-expanded={mobileDrawerOpen}
              aria-controls="mobile-nav-drawer"
              aria-label={mobileDrawerOpen ? 'بستن منو' : 'باز کردن منو'}
              whileTap={{ scale: 0.94 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileDrawerOpen ? (
                  <motion.span
                    key="close"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center justify-center"
                  >
                    <X className="h-[1.35rem] w-[1.35rem]" strokeWidth={2} aria-hidden />
                  </motion.span>
                ) : (
                  <motion.span
                    key="open"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center justify-center"
                  >
                    <Menu className="h-[1.35rem] w-[1.35rem]" strokeWidth={2} aria-hidden />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>

          <div className="hidden min-w-0 flex-1 items-center gap-3 lg:flex">
            {renderSearchCluster('flex-1 lg:max-w-xl')}
          </div>

          <div className="hidden shrink-0 items-center gap-2 lg:flex">
            <div
              className="relative"
              onMouseEnter={() => {
                cancelCartHoverClose();
                setCartPreviewOpen(true);
              }}
              onMouseLeave={scheduleCartHoverClose}
            >
              <motion.button
                type="button"
                onClick={goCart}
                whileTap={{ scale: 0.94 }}
                className={cartButtonClass}
                title={cartPayableTitle}
                aria-label={cartPayableTitle}
                aria-expanded={cartPreviewOpen}
              >
                <FontAwesomeIcon icon={faCartShopping} className="text-[13px] leading-none sm:text-sm" aria-hidden />
                {cartCount > 0 && (
                  <span className="absolute -top-1 end-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#1C3B9B] px-0.5 text-[10px] font-bold text-white ring-2 ring-white">
                    {cartCount > 9 ? '+' : cartCount.toLocaleString('fa-IR')}
                  </span>
                )}
              </motion.button>

              <AnimatePresence>
                {cartPreviewOpen && (
                  <motion.div
                    dir="rtl"
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute end-0 top-full z-[61] mt-1.5 w-[min(100vw-1.5rem,22.5rem)] overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_24px_56px_-12px_rgba(15,23,42,0.32)] ring-1 ring-slate-900/[0.05]"
                    onMouseEnter={cancelCartHoverClose}
                    onMouseLeave={scheduleCartHoverClose}
                  >
                    <div className="relative border-b border-slate-100 bg-gradient-to-l from-slate-50 to-white px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-extrabold text-slate-900">سبد خرید</p>
                          <p className="text-[10px] text-slate-500">
                            {cartPreviewLoading ? 'در حال بارگذاری…' : `${cartCount.toLocaleString('fa-IR')} کالا`}
                          </p>
                        </div>
                        {cartPreviewLoading ? <BrandSpinnerRing size="sm" /> : null}
                      </div>
                    </div>

                    {cartPreviewLoading ? (
                      <CartPopoverSkeleton />
                    ) : cartLines.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <ShoppingBag className="mx-auto mb-2 h-10 w-10 text-slate-200" strokeWidth={1.25} aria-hidden />
                        <p className="text-sm font-semibold text-slate-700">سبد خالی است</p>
                        <p className="mt-1 text-xs text-slate-500">از صفحه دوره‌ها محصول اضافه کنید</p>
                        <button
                          type="button"
                          onClick={() => {
                            setCartPreviewOpen(false);
                            navigate('/courses');
                          }}
                          className="mt-4 text-xs font-bold text-[#1C3B9B] underline-offset-2 hover:underline"
                        >
                          مشاهده دوره‌ها
                        </button>
                      </div>
                    ) : (
                      <>
                        <ul className="max-h-[min(50vh,15.5rem)] space-y-1 overflow-y-auto p-2.5">
                          <AnimatePresence initial={false}>
                            {cartLines.map((line) => {
                              const pct =
                                line.price > 0 && line.listPrice > line.price
                                  ? Math.round((1 - line.price / line.listPrice) * 100)
                                  : 0;
                              return (
                                <motion.li
                                  key={line.id}
                                  layout
                                  initial={{ opacity: 0, scale: 0.98 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, x: 14, scale: 0.98 }}
                                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                  className="overflow-hidden rounded-xl border border-slate-100 bg-slate-50/80 p-2.5 text-right shadow-sm"
                                >
                                  <div className="flex gap-2.5">
                                    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200/90 bg-slate-100 shadow-inner ring-1 ring-white/80">
                                      <img
                                        src={getImageUrlWithFallback(line.thumbnail, '/assets/homeV2/01.jpg')}
                                        alt=""
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                                        <p className="text-xs font-bold leading-snug text-slate-900">{line.title}</p>
                                        {pct > 0 && (
                                          <span className="shrink-0 rounded-md bg-[#C11277]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#C11277]">
                                            ٪{pct.toLocaleString('fa-IR')} تخفیف
                                          </span>
                                        )}
                                      </div>
                                      <p className="mt-0.5 text-[10px] text-slate-500">{line.subtitle}</p>
                                      <div className="mt-2 flex flex-wrap items-end justify-end gap-2">
                                        {line.price <= 0 ? (
                                          <FreeBadge size="sm" className="shadow-none" />
                                        ) : (
                                          <span className="text-sm font-extrabold tabular-nums text-[#1C3B9B]">
                                            {formatToman(line.price)}
                                          </span>
                                        )}
                                        {line.price > 0 && line.listPrice > line.price && (
                                          <span className="text-[11px] tabular-nums text-slate-400 line-through decoration-slate-300">
                                            {formatToman(line.listPrice)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeCartItem(line.id);
                                      }}
                                      className="flex h-8 w-8 shrink-0 self-start items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                                      title="حذف از سبد"
                                      aria-label={`حذف ${line.title}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" strokeWidth={2.2} />
                                    </button>
                                  </div>
                                </motion.li>
                              );
                            })}
                          </AnimatePresence>
                        </ul>

                        <div className="space-y-1.5 border-t border-slate-100 bg-slate-50/90 px-3 py-2.5">
                          <div className="flex items-center justify-between text-[11px] text-slate-600">
                            <span>جمع قبل از تخفیف</span>
                            <span className="font-semibold tabular-nums text-slate-700">
                              {formatToman(cartTotals.listSum)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-emerald-700">سود شما از تخفیف</span>
                            <span className="font-bold tabular-nums text-emerald-700">
                              {cartTotals.saved > 0 ? `−${formatToman(cartTotals.saved)}` : '—'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between border-t border-slate-200/80 pt-1.5 text-xs">
                            <span className="font-extrabold text-slate-800">مبلغ قابل پرداخت</span>
                            {cartTotals.paySum <= 0 ? (
                              <FreeBadge size="sm" className="shadow-none" />
                            ) : (
                              <span className="bg-gradient-to-l from-[#1C3B9B] to-[#C11277] bg-clip-text text-sm font-black tabular-nums text-transparent">
                                {formatToman(cartTotals.paySum)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="border-t border-slate-100 bg-white p-2">
                          <button
                            type="button"
                            onClick={goCart}
                            disabled={cartLines.length === 0}
                            className="w-full rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#C11277] py-2.5 text-center text-xs font-extrabold text-white shadow-md transition hover:opacity-[0.97] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            ادامه و تسویه
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {user ? (
              <>
                <button
                  type="button"
                  onClick={openLeadModal}
                  className={`hidden h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-xs font-bold text-slate-700 sm:inline-flex ${HEADER_CTRL_BTN_BORDER}`}
                  aria-label="درخواست مشاوره"
                >
                  <Headset className="h-3.5 w-3.5 shrink-0 text-slate-600" strokeWidth={2} aria-hidden />
                  مشاوره
                </button>
                <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setMegaOpen(null);
                    setIsUserMenuOpen(!isUserMenuOpen);
                  }}
                  className={`group flex items-center gap-2 rounded-xl bg-white py-1 pe-2.5 ps-1 ${HEADER_CTRL_BTN_BORDER}`}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600">
                    <CircleUserRound className="h-4.5 w-4.5" strokeWidth={2.2} aria-hidden />
                  </span>
                  <span className="hidden text-right xl:block">
                    <span className="block max-w-[7rem] truncate text-xs font-black text-slate-800">
                      {user.firstName || user.username}
                    </span>
                    <span className="block text-[10px] font-semibold text-slate-500">دسترسی سریع</span>
                  </span>
                  <ChevronDown open={isUserMenuOpen} className="text-slate-400 transition-colors group-hover:text-[#1C3B9B]" />
                </button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute left-0 z-[60] mt-1.5 w-[22rem] overflow-hidden rounded-2xl border border-slate-200 bg-white text-right shadow-[0_28px_62px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-900/[0.04] md:left-auto md:right-0"
                      dir="rtl"
                    >
                      <div className="border-b border-[#E6E9F8] bg-[linear-gradient(145deg,#F8FAFF_0%,#FFFFFF_52%,#F6F1FF_100%)] px-4 py-3">
                        <p className="text-sm font-black text-slate-900">دسترسی سریع حساب کاربری</p>
                        <p className="mt-1 text-[11px] font-medium text-slate-500">بخش موردنظرت را سریع باز کن</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 bg-[linear-gradient(180deg,#FFFFFF_0%,#FCFBFF_100%)] p-3">
                        {dashboardQuickLinks.map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.href}
                              type="button"
                              onClick={() => {
                                navigate(item.href);
                                setIsUserMenuOpen(false);
                              }}
                              className="group/item rounded-2xl border border-[#E2E8F3] bg-white px-3 py-2.5 text-right shadow-[0_10px_20px_-20px_rgba(15,23,42,0.45)] transition-all hover:-translate-y-0.5 hover:border-[#CFC5FF] hover:bg-[linear-gradient(140deg,#FFFFFF_0%,#F7F2FF_100%)] hover:shadow-[0_18px_30px_-20px_rgba(109,40,217,0.35)]"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-[#DDE5FF] bg-[#EEF4FF] text-[#1C3B9B] transition-all group-hover/item:border-[#D8CCFF] group-hover/item:bg-[#F0E8FF] group-hover/item:text-[#6d28d9]">
                                  <Icon className="h-4 w-4" strokeWidth={2} />
                                </span>
                                <ChevronLeft className="h-3.5 w-3.5 text-slate-300 transition-all group-hover/item:-translate-x-0.5 group-hover/item:text-[#6d28d9]" />
                              </div>
                              <p className="mt-2 text-xs font-black text-slate-800">{item.label}</p>
                              <p className="mt-0.5 text-[10px] font-medium text-slate-500">{item.subtitle}</p>
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 border-t border-[#E6E9F8] bg-[linear-gradient(180deg,#FAFBFF_0%,#F5F2FF_100%)] p-3">
                        <button
                          type="button"
                          onClick={() => {
                            goCart();
                            setIsUserMenuOpen(false);
                          }}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#D7DEED] bg-white px-3 py-2 text-xs font-black text-slate-700 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.5)] transition hover:-translate-y-0.5 hover:border-[#C8D6FF] hover:text-[#1C3B9B]"
                        >
                          <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} />
                          سبد خرید
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            openLeadModal();
                            setIsUserMenuOpen(false);
                          }}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#D7DEED] bg-white px-3 py-2 text-xs font-black text-slate-700 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.5)] transition hover:-translate-y-0.5 hover:border-[#C8D6FF] hover:text-[#1C3B9B]"
                        >
                          <Headset className="h-3.5 w-3.5" strokeWidth={2} />
                          درخواست مشاوره
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="block w-full border-t border-slate-100 px-3 py-2.5 text-right text-sm font-bold text-slate-700 transition hover:bg-red-50 hover:text-red-600"
                      >
                        خروج
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                </div>
              </>
            ) : (
              <>
                <LoginEntryButton
                  onClick={() => {
                    setMegaOpen(null);
                    navigate('/login');
                  }}
                />
                <button
                  type="button"
                  onClick={openLeadModal}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-xs font-bold text-slate-700 ${HEADER_CTRL_BTN_BORDER}`}
                  aria-label="درخواست مشاوره"
                >
                  <Headset className="h-3.5 w-3.5 shrink-0 text-slate-600" strokeWidth={2} aria-hidden />
                  مشاوره
                </button>
              </>
            )}
          </div>
        </div>

        <div
          className="relative hidden lg:block"
          onMouseEnter={cancelMegaClose}
          onMouseLeave={scheduleMegaClose}
        >
          <motion.div
            initial={false}
            animate={{
              maxHeight: navLinksExpanded ? 200 : 0,
              opacity: navLinksExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
            style={{ pointerEvents: navLinksExpanded ? 'auto' : 'none' }}
          >
            <div className="flex flex-wrap items-center justify-center gap-0.5 py-1">
              <NavLinkUnderline
                key="/"
                faIcon={faHouse}
                active={isActive('/')}
                onClick={() => {
                  setMegaOpen(null);
                  navigate('/');
                }}
              >
                خانه
              </NavLinkUnderline>

              <MegaNavTrigger
                faIcon={faGraduationCapSolid}
                open={megaOpen === 'courses'}
                active={isCoursesSection}
                onMouseEnter={() => openMega('courses')}
                onFocus={() => openMega('courses')}
                onClick={() => setMegaOpen((m) => (m === 'courses' ? null : 'courses'))}
              >
                دوره‌ها
                <ChevronDown open={megaOpen === 'courses'} className="text-slate-400" />
              </MegaNavTrigger>

              <MegaNavTrigger
                faIcon={faPodcast}
                open={megaOpen === 'podcasts'}
                active={isPodcastsSection}
                onMouseEnter={() => openMega('podcasts')}
                onFocus={() => openMega('podcasts')}
                onClick={() => setMegaOpen((m) => (m === 'podcasts' ? null : 'podcasts'))}
              >
                پادکست‌ها
                <ChevronDown open={megaOpen === 'podcasts'} className="text-slate-400" />
              </MegaNavTrigger>

              {navAfterHome.map((item) => (
                <NavLinkUnderline
                  key={item.path}
                  faIcon={item.faIcon}
                  active={isActive(item.path)}
                  onClick={() => {
                    setMegaOpen(null);
                    navigate(item.path);
                  }}
                >
                  {item.label}
                </NavLinkUnderline>
              ))}
            </div>
          </motion.div>

          <AnimatePresence>
            {megaOpen && navLinksExpanded && (
              <motion.div
                key={megaOpen}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-x-0 top-full z-[55] px-0 pt-1.5 pb-3"
                onMouseEnter={cancelMegaClose}
              >
                <div
                  dir="rtl"
                  lang="fa"
                  className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_28px_64px_-24px_rgba(15,23,42,0.22)] ring-1 ring-slate-900/[0.04] backdrop-blur-md"
                >
                  <div className="border-b border-violet-100/80 bg-gradient-to-l from-violet-50/60 via-white to-slate-50/40 px-4 py-3.5 text-start">
                    <div className="flex items-center justify-start gap-2">
                      <h3 className="text-sm font-extrabold tracking-tight text-slate-900">
                        {megaOpen === 'courses' ? 'دسته‌بندی دوره‌ها' : 'دسته‌بندی پادکست‌ها'}
                      </h3>
                      {megaOpen === 'courses' ? (
                        <GraduationCap className="h-5 w-5 shrink-0 text-[#7c3aed]" strokeWidth={2} aria-hidden />
                      ) : (
                        <Mic2 className="h-5 w-5 shrink-0 text-[#7c3aed]" strokeWidth={2} aria-hidden />
                      )}
                    </div>
                    <p className="mt-1.5 text-[11px] font-semibold leading-relaxed text-slate-500">
                      {megaOpen === 'courses'
                        ? 'مسیر یادگیری را از میان موضوعات انتخاب کنید.'
                        : 'موضوعات صوتی و محتوای پادکست را مرور کنید.'}
                    </p>
                  </div>
                  <div className="bg-slate-50/30 p-4">
                    <MegaColumns
                      columns={megaOpen === 'courses' ? courseMegaColumns : MOCK_PODCAST_COLUMNS}
                      onPick={(href) => {
                        navigate(href);
                        setMegaOpen(null);
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {typeof document !== 'undefined' &&
          createPortal(
            <AnimatePresence>
              {mobileDrawerOpen && (
                <motion.div ref={mobilePortalRef} className="fixed inset-0 z-[200] lg:hidden">
                  <motion.div
                    role="presentation"
                    aria-hidden
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]"
                    onClick={() => setMobileDrawerOpen(false)}
                  />
                  <motion.div
                    id="mobile-nav-drawer"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="mobile-drawer-title"
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'tween', duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute right-0 top-0 h-[100dvh] max-h-[100dvh] w-screen max-w-[100vw] will-change-transform"
                  >
                    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-white shadow-[-16px_0_48px_-12px_rgba(15,23,42,0.22)]">
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200/80 bg-white px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] pe-3 ps-5">
                      <div className="min-w-0 text-right">
                        <p id="mobile-drawer-title" className="text-base font-bold text-slate-900">
                          منو
                        </p>
                        <p className="mt-0.5 text-[10px] font-medium text-slate-500">جستجو و مسیرها</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMobileDrawerOpen(false)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 active:scale-95"
                        aria-label="بستن"
                      >
                        <X className="h-[1.2rem] w-[1.2rem]" strokeWidth={2} aria-hidden />
                      </button>
                    </div>

                    <div className="border-b border-slate-200/70 bg-white px-4 py-3">
                      <div className="flex items-center gap-2">
                        <motion.button
                          type="button"
                          onClick={() => {
                            setMobileDrawerOpen(false);
                            goCart();
                          }}
                          whileTap={{ scale: 0.96 }}
                          className={`${cartButtonClass} relative h-10 w-10 shrink-0 rounded-xl p-0`}
                          title={cartPayableTitle}
                          aria-label={cartPayableTitle}
                        >
                          <FontAwesomeIcon icon={faCartShopping} className="text-[13px] leading-none sm:text-sm" aria-hidden />
                          {cartCount > 0 ? (
                            <span className="absolute -top-0.5 end-0 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[#1C3B9B] px-0.5 text-[9px] font-bold text-white ring-2 ring-white">
                              {cartCount > 9 ? '+' : cartCount.toLocaleString('fa-IR')}
                            </span>
                          ) : null}
                        </motion.button>
                        {user ? (
                          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1C3B9B] text-xs font-semibold text-white">
                              {user.firstName?.[0] || user.email?.[0] || 'U'}
                            </span>
                            <button
                              type="button"
                              onClick={() => goNav('/dashboard')}
                              className="min-w-0 truncate rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-bold text-[#1C3B9B] transition hover:bg-slate-50"
                            >
                              داشبورد
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setMobileDrawerOpen(false);
                                handleLogout();
                              }}
                              className="shrink-0 rounded-lg px-2 py-2 text-xs font-semibold text-slate-500 transition hover:bg-slate-200/60 hover:text-red-600"
                            >
                              خروج
                            </button>
                          </div>
                        ) : (
                          <div className="min-w-0 flex-1">
                            <LoginEntryButton
                              fullWidth
                              onClick={() => {
                                setMegaOpen(null);
                                setMobileDrawerOpen(false);
                                navigate('/login');
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] overscroll-y-contain"
                      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                    >
                      <section>
                        <p className="mb-2 text-right text-[10px] font-bold tracking-wide text-slate-400">جستجو</p>
                        <div className="rounded-xl border border-slate-200/80 bg-white p-2.5">
                          {renderSearchCluster('w-full')}
                        </div>
                      </section>

                      <section>
                        <p className="mb-2 text-right text-[10px] font-bold tracking-wide text-slate-400">مسیرها</p>
                        <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white">
                          <button
                            type="button"
                            onClick={() => goNav('/')}
                            className={`group/navrow flex w-full items-center justify-between gap-2 border-b border-slate-100 px-3.5 py-3 text-right text-sm font-semibold transition-colors ${
                              isActive('/')
                                ? 'bg-[#3A68C2]/10 text-[#1C3B9B]'
                                : 'text-slate-700 hover:bg-slate-50/80'
                            }`}
                          >
                            <span className="flex min-w-0 flex-1 items-center gap-3">
                              <MobileDrawerRowIcon icon={Home} active={isActive('/')} />
                              <span className="min-w-0 transition-colors group-hover/navrow:text-[#1C3B9B]">
                                خانه
                              </span>
                            </span>
                            <ChevronLeft
                              className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover/navrow:text-[#1C3B9B]/45"
                              strokeWidth={2}
                              aria-hidden
                            />
                          </button>

                          <button
                            type="button"
                            onClick={() => setMobileCoursesOpen((o) => !o)}
                            className={`group/navrow flex w-full items-center justify-between px-3.5 py-3 text-right text-sm font-bold transition-colors ${
                              isCoursesSection
                                ? 'bg-[#3A68C2]/10 text-[#1C3B9B]'
                                : 'border-b border-slate-100 text-slate-800 hover:bg-slate-50/80'
                            }`}
                          >
                            <span className="flex min-w-0 flex-1 items-center gap-3">
                              <MobileDrawerRowIcon icon={GraduationCap} active={isCoursesSection || mobileCoursesOpen} />
                              <span className="min-w-0 transition-colors group-hover/navrow:text-[#1C3B9B]">
                                دوره‌ها
                              </span>
                            </span>
                            <ChevronDown
                              open={mobileCoursesOpen}
                              className="shrink-0 text-slate-400 transition-colors group-hover/navrow:text-[#1C3B9B]/55"
                            />
                          </button>
                          <AnimatePresence initial={false}>
                            {mobileCoursesOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                dir="rtl"
                                lang="fa"
                                className="overflow-hidden border-b border-slate-100 bg-slate-50/40"
                              >
                                <div className="space-y-4 p-2.5">
                                  {courseMegaColumns.map((col) => (
                                    <section key={col.id} dir="rtl" className="text-start">
                                      <h4 className="px-2 text-[11px] font-extrabold leading-tight text-slate-800">
                                        {col.title}
                                      </h4>
                                      {col.subtitle ? (
                                        <p className="mt-1 px-2 text-[10px] font-semibold leading-snug text-slate-500">
                                          {col.subtitle}
                                        </p>
                                      ) : null}
                                      <ul className="mt-2 space-y-0.5">
                                        {col.items.map((item) => (
                                          <li key={item.id}>
                                            <button
                                              type="button"
                                              onClick={() => goNav(item.href)}
                                              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-xs font-medium text-slate-600 transition hover:bg-white hover:text-[#1C3B9B]"
                                            >
                                              <MegaLinkThumb item={item} size="sm" />
                                              <span className="min-w-0 flex-1 truncate">{item.label}</span>
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    </section>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          <button
                            type="button"
                            onClick={() => setMobilePodcastsOpen((o) => !o)}
                            className={`group/navrow flex w-full items-center justify-between px-3.5 py-3 text-right text-sm font-bold transition-colors ${
                              isPodcastsSection
                                ? 'bg-[#3A68C2]/10 text-[#1C3B9B]'
                                : 'border-b border-slate-100 text-slate-800 hover:bg-slate-50/80'
                            }`}
                          >
                            <span className="flex min-w-0 flex-1 items-center gap-3">
                              <MobileDrawerRowIcon icon={Mic2} active={isPodcastsSection || mobilePodcastsOpen} />
                              <span className="min-w-0 transition-colors group-hover/navrow:text-[#1C3B9B]">
                                پادکست‌ها
                              </span>
                            </span>
                            <ChevronDown
                              open={mobilePodcastsOpen}
                              className="shrink-0 text-slate-400 transition-colors group-hover/navrow:text-[#1C3B9B]/55"
                            />
                          </button>
                          <AnimatePresence initial={false}>
                            {mobilePodcastsOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                dir="rtl"
                                lang="fa"
                                className="overflow-hidden border-b border-slate-100 bg-slate-50/40"
                              >
                                <div className="space-y-4 p-2.5">
                                  {MOCK_PODCAST_COLUMNS.map((col) => (
                                    <section key={col.id} dir="rtl" className="text-start">
                                      <h4 className="px-2 text-[11px] font-extrabold leading-tight text-slate-800">
                                        {col.title}
                                      </h4>
                                      {col.subtitle ? (
                                        <p className="mt-1 px-2 text-[10px] font-semibold leading-snug text-slate-500">
                                          {col.subtitle}
                                        </p>
                                      ) : null}
                                      <ul className="mt-2 space-y-0.5">
                                        {col.items.map((item) => (
                                          <li key={item.id}>
                                            <button
                                              type="button"
                                              onClick={() => goNav(item.href)}
                                              className="block w-full rounded-lg px-2.5 py-2 text-start text-xs font-medium text-slate-600 transition hover:bg-white hover:text-[#1C3B9B]"
                                            >
                                              {item.label}
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    </section>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {navAfterHome.map((item) => (
                            <button
                              key={item.path}
                              type="button"
                              onClick={() => goNav(item.path)}
                              className={`group/navrow flex w-full items-center justify-between gap-2 border-t border-slate-50 px-3.5 py-3 text-right text-sm font-semibold transition-colors ${
                                isActive(item.path)
                                ? 'bg-[#3A68C2]/10 text-[#1C3B9B]'
                                : 'text-slate-700 hover:bg-slate-50/80'
                              }`}
                            >
                              <span className="flex min-w-0 flex-1 items-center gap-3">
                                <MobileDrawerRowIcon icon={item.icon} active={isActive(item.path)} />
                                <span className="min-w-0 transition-colors group-hover/navrow:text-[#1C3B9B]">
                                  {item.label}
                                </span>
                              </span>
                              <ChevronLeft
                                className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover/navrow:text-[#1C3B9B]/45"
                                strokeWidth={2}
                                aria-hidden
                              />
                            </button>
                          ))}
                        </div>
                      </section>
                    </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body,
          )}
      </div>
    </nav>

    {typeof document !== 'undefined' ? createPortal(leadModalOverlay, document.body) : null}
    </>
  );
};

export default HeaderBar;
